import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bell, Megaphone, BadgeDollarSign, Wallet, Check } from 'lucide-react';

const API = `${window.location.origin}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

const TYPE_META = {
  announcement: { icon: Megaphone, color: '#D4AF37' },
  commission: { icon: BadgeDollarSign, color: '#2E9B50' },
  withdrawal: { icon: Wallet, color: '#D4AF37' },
};

/**
 * Header notifications bell for ambassadors.
 *
 * Polls unread-count every 60s for the badge, fetches the full feed on
 * open. Compiles three streams into a single dropdown:
 *   • Announcements (pinned/unread first)
 *   • Commission earned events
 *   • Withdrawal status updates
 *
 * Mark-all-read is one tap. Closing the dropdown also marks-as-read
 * anything the user actually scrolled past (left to next iteration —
 * for now we only mark on explicit action).
 */
export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [feed, setFeed] = useState({ items: [], unread_count: 0 });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  // Poll unread count
  useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      try {
        const r = await axios.get(`${API}/ambassador/me/notifications/unread-count`, { headers: authHeaders() });
        if (!cancelled) setFeed(f => ({ ...f, unread_count: r.data?.unread_count || 0 }));
      } catch { /* silent */ }
    };
    ping();
    const i = setInterval(ping, 30_000);
    return () => { cancelled = true; clearInterval(i); };
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const openPanel = async () => {
    setOpen(true);
    if (feed.items.length === 0) {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/ambassador/me/notifications`, { headers: authHeaders() });
        setFeed(r.data);
      } catch { /* silent */ }
      setLoading(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ambassador/me/notifications`, { headers: authHeaders() });
      setFeed(r.data);
    } catch { /* silent */ }
    setLoading(false);
  };

  const markRead = async (notifId) => {
    setFeed(prev => ({
      ...prev,
      items: prev.items.map(it => it.notif_id === notifId ? { ...it, is_read: true } : it),
      unread_count: Math.max(0, prev.unread_count - 1),
    }));
    try {
      await axios.post(`${API}/ambassador/me/notifications/${encodeURIComponent(notifId)}/read`, {}, { headers: authHeaders() });
    } catch { /* silent — UI already reflects */ }
  };

  const markAll = async () => {
    setFeed(prev => ({
      ...prev,
      items: prev.items.map(it => ({ ...it, is_read: true })),
      unread_count: 0,
    }));
    try {
      await axios.post(`${API}/ambassador/me/notifications/mark-all-read`, {}, { headers: authHeaders() });
    } catch { /* silent */ }
  };

  return (
    <div className="relative" ref={ref} data-testid="amb-notifications">
      <button
        onClick={open ? () => setOpen(false) : openPanel}
        className="relative p-2 text-white/60 hover:text-white transition-colors"
        data-testid="amb-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />
        {feed.unread_count > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full bg-[#D4AF37] text-black px-1"
            data-testid="amb-bell-badge"
          >
            {feed.unread_count > 99 ? '99+' : feed.unread_count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[360px] max-w-[calc(100vw-32px)] bg-[#0F0F0F] border border-white/10 shadow-2xl z-50"
          data-testid="amb-bell-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <div className="flex items-center gap-2">
              <Bell className="w-3.5 h-3.5 text-[#D4AF37]" />
              <h3 className="text-white text-sm font-sans font-medium">Notifications</h3>
            </div>
            {feed.unread_count > 0 && (
              <button onClick={markAll} data-testid="amb-mark-all-btn"
                className="text-[#D4AF37] text-[10px] uppercase tracking-wider hover:text-white font-sans flex items-center gap-1">
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto" data-testid="amb-notif-list">
            {loading && <p className="text-white/30 text-xs py-8 text-center font-sans">Loading…</p>}

            {!loading && feed.items.length === 0 && (
              <div className="px-4 py-10 text-center">
                <Bell className="w-8 h-8 text-white/10 mx-auto mb-2" />
                <p className="text-white/30 text-xs font-sans">All quiet right now.<br />New announcements, sales &amp; payouts appear here.</p>
              </div>
            )}

            {feed.items.map(item => {
              const meta = TYPE_META[item.type] || TYPE_META.announcement;
              const Icon = meta.icon;
              return (
                <button
                  key={item.notif_id}
                  onClick={() => !item.is_read && markRead(item.notif_id)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-white/5 transition-colors ${item.is_read ? 'opacity-50' : 'hover:bg-white/[0.02]'}`}
                  data-testid={`notif-row-${item.type}`}
                >
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                       style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}40` }}>
                    <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-white text-xs font-sans font-medium truncate">{item.title}</p>
                      {!item.is_read && (
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] flex-shrink-0 mt-1.5" data-testid="notif-dot" />
                      )}
                    </div>
                    {item.body && <p className="text-white/50 text-xs font-sans mt-0.5 line-clamp-2">{item.body}</p>}
                    <p className="text-white/30 text-[10px] font-sans mt-1">
                      {item.created_at ? new Date(item.created_at).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {feed.items.length > 0 && (
            <button onClick={refresh} className="w-full text-center text-white/40 text-[10px] uppercase tracking-wider py-2 hover:text-white border-t border-white/5 font-sans">
              Refresh
            </button>
          )}
        </div>
      )}
    </div>
  );
}
